// Accounting views are lazy-loaded in the routes below to avoid build errors if files are missing
import AccountingSettingsView from '@/views/accounting/AccountingSettingsView.vue'
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import LoginView from '@/views/LoginView.vue'
import DashboardLayout from '@/layouts/DashboardLayout.vue'

type NavigationNodeLike = {
  id: string
  path?: string | null
  visibleInSidebar?: boolean
  kind?: string | null
  children?: NavigationNodeLike[]
}

function collectProtectedRoutesFromNavigation(categories: Array<{ items?: NavigationNodeLike[] }>) {
  const routeMap: Record<string, string> = {}

  const visit = (item: NavigationNodeLike) => {
    const path = item.path?.trim()
    if (path && path !== '/' && path.startsWith('/') && item.kind !== 'PERSONAL' && item.kind !== 'GROUP' && item.visibleInSidebar !== false) {
      routeMap[path] = item.id
    }

    for (const child of item.children || []) {
      visit(child)
    }
  }

  for (const category of categories) {
    for (const item of category.items || []) {
      visit(item)
    }
  }

  return routeMap
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: { title: 'Connexion', requiresAuth: false }
    },
    {
      path: '/feedback/:token',
      name: 'feedback-public',
      component: () => import('@/views/FeedbackPublicView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/accounting/pcg',
      name: 'accounting-pcg',
      component: () => import('@/views/accounting/ChartOfAccountsView.vue'),
      meta: { title: 'PCG', requiresAuth: true, menuId: 'accounting-pcg' }
    },
    {
      path: '/accounting/periods',
      name: 'accounting-periods',
      component: () => import('@/views/accounting/AccountingPeriodsView.vue'),
      meta: { title: 'Exercices comptables', requiresAuth: true, menuId: 'accounting-periods' }
    },
    {
      path: '/accounting/journal-sales',
      name: 'accounting-journal-sales',
      component: () => import('@/views/accounting/SalesJournalView.vue'),
      meta: { title: 'Journal des ventes', requiresAuth: true, menuId: 'journal-sales' }
    },
    {
      path: '/accounting/journal-purchases',
      name: 'accounting-journal-purchases',
      component: () => import('@/views/accounting/PurchasesJournalView.vue'),
      meta: { title: 'Journal des achats', requiresAuth: true, menuId: 'journal-purchases' }
    },
    {
      path: '/accounting/journal-bank',
      name: 'accounting-journal-bank',
      component: () => import('@/views/accounting/BankJournalView.vue'),
      meta: { title: 'Journal de banque', requiresAuth: true, menuId: 'journal-bank' }
    },
    {
      path: '/accounting/journal',
      name: 'accounting-journal',
      component: () => import('@/views/accounting/AccountingJournalView.vue'),
      meta: { title: 'Journal', requiresAuth: true, menuId: 'accounting-journal' }
    },
    {
      path: '/accounting/taxes/tva',
      name: 'accounting-taxes-tva',
      component: () => import('@/views/accounting/TaxTvaView.vue'),
      meta: { title: 'TVA', requiresAuth: true, menuId: 'accounting-tva' }
    },
    {
      path: '/accounting/taxes/local',
      name: 'accounting-taxes-local',
      component: () => import('@/views/accounting/TaxLocalView.vue'),
      meta: { title: 'Impôts locaux', requiresAuth: true, menuId: 'accounting-taxes-local' }
    },
    {
      path: '/accounting/income-statement',
      name: 'accounting-income-statement',
      component: () => import('@/views/accounting/IncomeStatementView.vue'),
      meta: { title: 'Compte de résultat', requiresAuth: true, menuId: 'accounting-income-statement' }
    },
    {
      path: '/accounting/settings',
      name: 'accounting-settings',
      component: AccountingSettingsView,
      meta: { title: 'Paramètres comptables', requiresAuth: true, menuId: 'accounting-settings' }
    },
    {
      path: '/',
      component: DashboardLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/views/DashboardView.vue')
        },
        {
          path: 'inventory',
          name: 'inventory',
          component: () => import('@/views/InventoryView.vue'),
          meta: { requiresAuth: true, menuId: 'inventory' }
        },
        {
          path: 'employees',
          name: 'employees',
          component: () => import('@/views/EmployeesView.vue'),
          meta: { requiresAuth: true, menuId: 'employees' }
        },
        {
          path: 'departments',
          name: 'departments',
          component: () => import('@/views/DepartmentsView.vue'),
          meta: { requiresAuth: true, menuId: 'departments' }
        },
        {
          path: 'roles',
          name: 'roles',
          component: () => import('@/views/RolesView.vue'),
          meta: { requiresAuth: true, menuId: 'roles' }
        },
        {
          path: 'attendances',
          name: 'attendances',
          component: () => import('@/views/AttendancesView.vue'),
          meta: { requiresAuth: true, menuId: 'attendances' }
        },
        {
          path: 'leaves',
          name: 'leaves',
          component: () => import('@/views/LeavesView.vue')
        },
        {
          path: 'calendar',
          name: 'calendar',
          component: () => import('@/views/CalendarView.vue')
        },
        {
          path: 'my-leaves',
          name: 'my-leaves',
          component: () => import('@/views/MyLeavesView.vue')
        },
        {
          path: 'bug-reports',
          name: 'bug-reports',
          component: () => import('@/views/BugReportsView.vue')
        },
        {
          path: 'my-attendance',
          name: 'my-attendance',
          component: () => import('@/views/MyAttendanceView.vue')
        },
        {
          path: 'my-timesheets',
          name: 'my-timesheets',
          component: () => import('@/views/MyTimesheetsView.vue'),
          meta: { requiresAuth: true, menuId: 'my-timesheets' }
        },
        {
          path: 'todo-list',
          name: 'todo-list',
          component: () => import('@/views/TodoListView.vue'),
          meta: { requiresAuth: true, menuId: 'todo-list' }
        },
        {
          path: 'costings',
          name: 'costings',
          component: () => import('@/views/CostingsView.vue'),
          meta: { requiresAuth: true, menuId: 'costings' }
        },
        {
          path: 'projects',
          name: 'projects',
          component: () => import('@/views/ProjectsView.vue'),
          meta: { requiresAuth: true, menuId: 'projects' }
        },

        {
          path: 'leave-validation',
          name: 'leave-validation',
          component: () => import('@/views/LeaveValidationView.vue'),
          meta: { requiresAuth: true, menuId: 'leave-validation' }
        },
        {
          path: 'timesheets-rh',
          name: 'timesheets-rh',
          component: () => import('@/views/RhTimesheetsView.vue'),
          meta: { requiresAuth: true, menuId: 'timesheets-rh' }
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('@/views/SettingsView.vue'),
          meta: { requiresAuth: true, menuId: 'settings' }
        },
        {
          path: 'company',
          name: 'company',
          component: () => import('@/views/CompanySettingsView.vue'),
          meta: { requiresAuth: true, menuId: 'company-info' }
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('@/views/ProfileView.vue')
        },
        {
          path: 'categories',
          name: 'categories',
          component: () => import('@/views/CategoriesView.vue'),
          meta: { requiresAuth: true, menuId: 'categories' }
        },
        {
          path: 'aisles',
          name: 'aisles',
          component: () => import('@/views/AislesView.vue'),
          meta: { requiresAuth: true, menuId: 'aisles' }
        },
        {
          path: 'movements',
          name: 'movements',
          component: () => import('@/views/MovementsView.vue'),
          meta: { requiresAuth: true, menuId: 'movements' }
        },
        {
          path: 'usages',
          name: 'usages',
          component: () => import('@/views/UsagesView.vue'),
          meta: { requiresAuth: true, menuId: 'usages' }
        },
        {
          path: 'logistics-dashboard',
          name: 'logistics-dashboard',
          component: () => import('@/views/LogisticsDashboardView.vue'),
          meta: { requiresAuth: true, menuId: 'logistics-dashboard' }
        },
        {
          path: 'companies',
          name: 'companies',
          component: () => import('@/views/CompaniesView.vue'),
          meta: { requiresAuth: true, menuId: 'companies' }
        },
        {
          path: 'warranty',
          name: 'warranty',
          component: () => import('@/views/WarrantyView.vue')
        },
        {
          path: 'contacts',
          name: 'contacts',
          component: () => import('@/views/ContactsView.vue'),
          meta: { requiresAuth: true, menuId: 'contacts' }
        },
        {
          path: 'opportunities',
          name: 'opportunities',
          component: () => import('@/views/OpportunitiesView.vue'),
          meta: { requiresAuth: true, menuId: 'opportunities' }
        },
        {
          path: 'quotes',
          name: 'quotes',
          component: () => import('@/views/QuotesView.vue'),
          meta: { requiresAuth: true, menuId: 'quotes' }
        },
        {
          path: 'purchase-orders',
          name: 'purchase-orders',
          component: () => import('@/views/PurchaseOrdersView.vue'),
          meta: { requiresAuth: true, menuId: 'purchase-orders' }
        },
        {
          path: 'delivery-notes',
          name: 'delivery-notes',
          component: () => import('@/views/DeliveryNotesView.vue'),
          meta: { requiresAuth: true, menuId: 'delivery-notes' }
        },
        {
          path: 'invoices',
          name: 'invoices',
          component: () => import('@/views/InvoicesView.vue'),
          meta: { requiresAuth: true, menuId: 'invoices' }
        },
        {
          path: 'invoices-clients',
          name: 'invoices-clients',
          component: () => import('@/views/ClientInvoicesView.vue')
        },
        {
          path: 'accounting-dashboard',
          name: 'accounting-dashboard',
          component: () => import('@/views/AccountingDashboardView.vue'),
          meta: { requiresAuth: true, menuId: 'accounting-dashboard' }
        },
        {
          path: 'supplier-invoices',
          name: 'supplier-invoices',
          component: () => import('@/views/SupplierInvoicesView.vue'),
          meta: { requiresAuth: true, menuId: 'supplier-invoices' }
        },
        {
          path: 'payments',
          name: 'payments',
          component: () => import('@/views/PaymentsView.vue'),
          meta: { requiresAuth: true, menuId: 'payments' }
        },
        {
          path: 'transactions',
          name: 'transactions',
          component: () => import('@/views/TransactionsView.vue'),
          meta: { requiresAuth: true, menuId: 'transactions' }
        },
        {
          path: 'cashboxes',
          name: 'cashboxes',
          component: () => import('@/views/CashBoxesView.vue'),
          meta: { requiresAuth: true, menuId: 'cashboxes' }
        },
        {
          path: 'cash-approvision',
          name: 'cash-approvision',
          component: () => import('@/views/CashApprovisionView.vue'),
          meta: { requiresAuth: true, menuId: 'cash-approvision' }
        },
        {
          path: 'treasury-transfers',
          name: 'treasury-transfers',
          component: () => import('@/views/TreasuryTransfersView.vue'),
          meta: { requiresAuth: true, menuId: 'treasury-transfers' }
        },
        {
          path: 'cash-journal',
          name: 'cash-journal',
          component: () => import('@/views/CashJournalView.vue'),
          meta: { requiresAuth: true, menuId: 'cash-journal' }
        },
        {
          path: 'banks',
          name: 'banks',
          component: () => import('@/views/BanksView.vue'),
          meta: { requiresAuth: true, menuId: 'banks' }
        },
        {
          path: 'treasury-operation-types',
          name: 'treasury-operation-types',
          component: () => import('@/views/TreasuryOperationTypesView.vue'),
          meta: { requiresAuth: true, menuId: 'treasury-operation-types' }
        },
        {
          path: 'journal-entries',
          name: 'journal-entries',
          component: () => import('@/views/JournalEntriesView.vue'),
          meta: { requiresAuth: true, menuId: 'journal-entries' }
        },
        // Duplicate routes removed: cashboxes/cash-approvision were defined twice
        {
          path: 'ledger',
          name: 'ledger',
          component: () => import('@/views/accounting/LedgerView.vue'),
          meta: { requiresAuth: true, menuId: 'ledger' }
        },
        {
          path: 'balance',
          name: 'balance',
          component: () => import('@/views/accounting/BalanceView.vue'),
          meta: { requiresAuth: true, menuId: 'balance' }
        },
        {
          path: 'financial-reports',
          name: 'financial-reports',
          component: () => import('@/views/FinancialReportsView.vue'),
          meta: { requiresAuth: true, menuId: 'financial-reports' }
        },
        {
          path: 'accounting/pcg',
          name: 'accounting-pcg',
          component: () => import('@/views/accounting/ChartOfAccountsView.vue')
        },
        {
          path: 'accounting/periods',
          name: 'accounting-periods',
          component: () => import('@/views/accounting/AccountingPeriodsView.vue')
        },
        {
          path: 'accounting/journal-sales',
          name: 'accounting-journal-sales',
          component: () => import('@/views/accounting/SalesJournalView.vue')
        },
        {
          path: 'accounting/journal-purchases',
          name: 'accounting-journal-purchases',
          component: () => import('@/views/accounting/PurchasesJournalView.vue')
        },
        {
          path: 'accounting/journal-bank',
          name: 'accounting-journal-bank',
          component: () => import('@/views/accounting/BankJournalView.vue')
        },
        {
          path: 'accounting/taxes/tva',
          name: 'accounting-tva',
          component: () => import('@/views/accounting/TaxTvaView.vue')
        },
        {
          path: 'accounting/taxes/local',
          name: 'accounting-taxes-local',
          component: () => import('@/views/accounting/TaxLocalView.vue')
        },
        {
          path: 'accounting/income-statement',
          name: 'accounting-income-statement',
          component: () => import('@/views/accounting/IncomeStatementView.vue')
        },
        {
          path: 'sales-dashboard',
          name: 'sales-dashboard',
          component: () => import('@/views/SalesDashboardView.vue'),
          meta: { requiresAuth: true, menuId: 'sales-dashboard' }
        },
        {
          path: 'purchases-dashboard',
          name: 'purchases-dashboard',
          component: () => import('@/views/PurchasesDashboardView.vue'),
          meta: { requiresAuth: true, menuId: 'purchases-dashboard' }
        },
        {
          path: 'suppliers',
          name: 'suppliers',
          component: () => import('@/views/SuppliersView.vue'),
          meta: { requiresAuth: true, menuId: 'suppliers' }
        },
        {
          path: 'suppliers-contacts',
          name: 'suppliers-contacts',
          component: () => import('@/views/SuppliersContactsView.vue'),
          meta: { requiresAuth: true, menuId: 'suppliers-contacts' }
        },
        {
          path: 'purchase-quotes',
          name: 'purchase-quotes',
          component: () => import('@/views/PurchaseQuotesView.vue'),
          meta: { requiresAuth: true, menuId: 'purchase-quotes' }
        },
        {
          path: 'purchase-orders-suppliers',
          name: 'purchase-orders-suppliers',
          component: () => import('@/views/SupplierPurchaseOrdersView.vue'),
          meta: { requiresAuth: true, menuId: 'purchase-orders-suppliers' }
        },
        {
          path: 'purchase-deliveries',
          name: 'purchase-deliveries',
          component: () => import('@/views/PurchaseDeliveriesView.vue'),
          meta: { requiresAuth: true, menuId: 'purchase-deliveries' }
        },
        {
          path: 'purchase-invoices',
          name: 'purchase-invoices',
          component: () => import('@/views/PurchaseInvoicesView.vue'),
          meta: { requiresAuth: true, menuId: 'purchase-invoices' }
        },
        {
          path: 'expense-dossiers',
          name: 'expense-dossiers',
          component: () => import('@/views/ExpenseDossiersView.vue'),
          meta: { requiresAuth: true, menuId: 'expense-dossiers' }
        }
      ]
    }
  ]
})

// Guard de navigation pour l'authentification
router.beforeEach(async (to) => {
  const authStore = useAuthStore()

  // Si l'initialisation n'est pas encore faite, attendre le résultat du checkAuth.
  // Ceci évite qu'une route 'protected' soit chargée alors que l'authentification
  // n'a pas encore été validée.
  if (!authStore.initialized) {
    try {
      await authStore.checkAuth()
    } catch (e) {
      // Silent: checkAuth gère le clearAuth en cas d'erreur
    }
  }
  
  if (to.meta.requiresAuth !== false && !authStore.isAuthenticated) {
    return { name: 'login' }
  }
  
  if (to.name === 'login' && authStore.isAuthenticated) {
    // Rediriger vers le premier dashboard accessible
    const { usePermissionsStore } = await import('@/stores/permissions')
    const permissionsStore = usePermissionsStore()
    
    // Attendre que les permissions soient chargées
    if (!permissionsStore.initialized) {
      await permissionsStore.fetchMenuItems()
    }
    
    // Ordre de priorité des dashboards
    const dashboards = [
      { id: 'dashboard-rh', path: '/' },
      { id: 'logistics-dashboard', path: '/logistics-dashboard' },
      { id: 'sales-dashboard', path: '/sales-dashboard' },
      { id: 'accounting-dashboard', path: '/accounting-dashboard' }
    ]
    
    // Trouver le premier dashboard accessible
    for (const dashboard of dashboards) {
      if (authStore.isAdmin || permissionsStore.hasAccess(dashboard.id, 'view')) {
        return dashboard.path
      }
    }
    
    return { name: 'dashboard' } // Fallback
  }
  
  // Si on accède à la racine /, rediriger vers le premier dashboard accessible
  if (to.path === '/' && authStore.isAuthenticated) {
    const { usePermissionsStore } = await import('@/stores/permissions')
    const permissionsStore = usePermissionsStore()
    
    // Attendre que les permissions soient chargées
    if (!permissionsStore.initialized) {
      await permissionsStore.fetchMenuItems()
    }
    
    const dashboards = [
      { id: 'dashboard-rh', path: '/' },
      { id: 'logistics-dashboard', path: '/logistics-dashboard' },
      { id: 'sales-dashboard', path: '/sales-dashboard' },
      { id: 'accounting-dashboard', path: '/accounting-dashboard' }
    ]
    
    for (const dashboard of dashboards) {
      if (authStore.isAdmin || permissionsStore.hasAccess(dashboard.id, 'view')) {
        if (dashboard.path !== '/') {
          return dashboard.path
        }
        // Si c'est déjà /, laisser passer
        return
      }
    }
  }

  if (authStore.isAuthenticated && !authStore.isAdmin) {
    const { usePermissionsStore } = await import('@/stores/permissions')
    const permissionsStore = usePermissionsStore()

    await permissionsStore.fetchMenuItems()

    const authorizedNavigation = permissionsStore.fullNavigationTree.length
      ? permissionsStore.fullNavigationTree
      : permissionsStore.navigationTree

    const routeProtectionMap = collectProtectedRoutesFromNavigation(
      authorizedNavigation as Array<{ items?: Array<{ id: string; path?: string | null; visibleInSidebar?: boolean; kind?: string | null; children?: any[] }> }>
    )

    const requiredMenuId = typeof to.meta.menuId === 'string'
      ? to.meta.menuId
      : routeProtectionMap[to.path]

    if (requiredMenuId && !permissionsStore.hasAccess(requiredMenuId, 'view')) {
      return '/my-attendance'
    }
  }
})

export default router